import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, FileText, Users, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const resourcesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Pricing', path: '/pricing' }
  ];

  const resourcesDropdown = [
    {
      title: 'Blog',
      description: 'Insights, tips, and best practices for modern HR',
      icon: FileText,
      path: '/blog',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'Customer Stories',
      description: 'Real success stories from our amazing customers',
      icon: Users,
      // path: '/customer-stories',
      path: '',
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform duration-200">
              <span className="text-white font-bold text-xl">H</span>
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
              HRMQore
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 hover:text-[#0066FF] ${
                  location.pathname === link.path
                    ? 'text-[#0066FF]'
                    : isScrolled
                    ? 'text-gray-700'
                    : 'text-gray-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Resources Dropdown */}
            <div className="relative" ref={resourcesRef}>
              <button
                onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                className={`flex items-center space-x-1 text-sm font-medium transition-colors duration-200 hover:text-[#0066FF] ${
                  location.pathname === '/resources'
                    ? 'text-[#0066FF]'
                    : isScrolled
                    ? 'text-gray-700'
                    : 'text-gray-100'
                }`}
              >
                <span>Resources</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isResourcesOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Mega Dropdown */}
              {isResourcesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[480px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* Dropdown Header */}
                  <div className="bg-gradient-to-r from-[#0066FF]/5 to-[#0052CC]/5 px-6 py-4 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">Resources</p>
                    <p className="text-xs text-gray-500">Explore our knowledge hub</p>
                  </div>
                  
                  {/* Dropdown Items */}
                  <div className="p-4 grid gap-2">
                    {resourcesDropdown.map((item) => (
                      <Link
                        key={item.title}
                        to={item.path}
                        onClick={() => setIsResourcesOpen(false)}
                        className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
                      >
                        <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                          <item.icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900 group-hover:text-[#0066FF] transition-colors">
                              {item.title}
                            </h4>
                            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#0066FF] group-hover:translate-x-1 transition-all duration-200" />
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Dropdown Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Discover insights and success stories from our community
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Link */}
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors duration-200 hover:text-[#0066FF] ${
                location.pathname === '/contact'
                  ? 'text-[#0066FF]'
                  : isScrolled
                  ? 'text-gray-700'
                  : 'text-gray-100'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 transition-opacity"
              onClick={() => window.location.href = '/#demo'}
            >
              Request Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-base font-medium transition-colors duration-200 hover:text-[#0066FF] ${
                  location.pathname === link.path
                    ? 'text-[#0066FF]'
                    : 'text-gray-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Resources Section */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Resources</p>
              {resourcesDropdown.map((item) => (
                <Link
                  key={item.title}
                  to={item.path}
                  className="flex items-center space-x-3 py-2 text-gray-700 hover:text-[#0066FF]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center`}>
                    <item.icon className="w-4 h-4" />
                  </div>
                  <span className="font-medium">{item.title}</span>
                </Link>
              ))}
            </div>

            {/* Contact Link */}
            <Link
              to="/contact"
              className="block text-base font-medium transition-colors duration-200 hover:text-[#0066FF] text-gray-700 border-t border-gray-100 pt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>

            <div className="pt-4 space-y-2">
              <Button
                className="w-full bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white"
                onClick={() => {
                  window.location.href = '/#demo';
                  setIsMobileMenuOpen(false);
                }}
              >
                Request Demo
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;